import { getPatientWidget } from 'patient-api-lib'
import { Suspense, useEffect, useState } from 'react'
import { useLocation, useNavigate, useOutlet } from 'react-router-dom'
import { useSessionContext } from 'supertokens-auth-react/recipe/session'
import { attemptRefreshingSession } from 'supertokens-web-js/recipe/session'

import i18next from '@/i18n/i18n'

import { RouterPath } from './models/enums'
import LocalStorageService from './services/LocalStorageService'
import { useAppStore } from './store/app'
import { growthbook } from './utils/growthbook'
import { rudderAnalytics } from './utils/rudderstack'

function App() {
  const outlet = useOutlet()
  const location = useLocation()
  const navigate = useNavigate()
  const session = useSessionContext()
  const searchParams = new URLSearchParams(location.search)
  const { clinicIds } = useAppStore((state) => state)
  const { setClinicIds, setCustomerServiceNumber } = useAppStore((state) => state)
  const [startingApp, setStartingApp] = useState<boolean>(true)

  useEffect(() => {
    if (clinicIds) return

    const fetchData = async () => {
      const responseConfig = await getPatientWidget()
      const config = responseConfig.getBookingConfig()
      const clinicIdsList = config?.getClinicIdsList()

      return { clinicIdsList, customerServiceNumber: config?.getCustomerServicePhone()?.getE164Number() }
    }

    fetchData()
      .then(({ clinicIdsList, customerServiceNumber }) => {
        if (!clinicIdsList || !customerServiceNumber)
          throw new Error('Clinic IDs and Customer Service Number are missing')
        setClinicIds(clinicIdsList)
        setCustomerServiceNumber(customerServiceNumber)
      })
      .catch((error) => {
        throw new Error(error)
      })
      .finally(() => {
        setStartingApp(false)
      })
  }, [clinicIds, setClinicIds, setCustomerServiceNumber])

  useEffect(() => {
    if (session.loading) return

    if (searchParams.get('r')) {
      LocalStorageService.clearData('frontendCookies')
      attemptRefreshingSession()
      navigate(`/${RouterPath.Hub}/${RouterPath.Home}`, { replace: true })
      return
    }

    if (!session.doesSessionExist) {
      navigate(`/${RouterPath.Login}`)
      return
    }

    rudderAnalytics.ready(() => {
      const anonymousId = rudderAnalytics.getAnonymousId()
      if (!anonymousId) return

      rudderAnalytics.identify(anonymousId, {
        lang: i18next.language,
        user_id: session.userId,
      })
    })

    if (location.pathname === `/${RouterPath.Login}` && session.doesSessionExist) {
      navigate(`/${RouterPath.Hub}/${RouterPath.Home}`)
      return
    }

    if (location.pathname !== `/${RouterPath.Login}`) {
      if (location.pathname === RouterPath.Root) {
        navigate(`/${RouterPath.Hub}/${RouterPath.Home}`)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session, navigate])

  useEffect(() => {
    growthbook.setAttributes({
      id: rudderAnalytics.getAnonymousId(),
      company: 'aqua_dental',
    })
  }, [])

  return <div className="min-h-dvh">{!startingApp && <Suspense fallback={<div />}>{outlet}</Suspense>}</div>
}

export default App
